import { LightweightCourseV5 } from '../types'

export type MigrationStatus =
  | { kind: 'needsMigration'; eligible: true }
  | { kind: 'needsMigration'; eligible: false; reason: 'multiClass' }
  | { kind: 'migrating' }
  | { kind: 'none' }

export type MigrationStatusDisplay = {
  iconType: 'warning' | 'info' | 'sync'
  iconColor: string
  label: string
  color: 'warning' | 'info' | 'error' | 'default'
  tooltipText: string
}

/**
 * Determines the migration status of a course based on version properties and class count
 * @param course - The course to check
 * @param classCount - Number of classes associated with the course
 * @returns Migration status object
 */
export let getMigrationStatus = (
  course: LightweightCourseV5,
  classCount: number,
): MigrationStatus => {
  // Handle V1 courses (no version property or version: 1)
  if (!course.version || course.version === 1) {
    if (classCount <= 1) {
      return { kind: 'needsMigration', eligible: true }
    }
    if (classCount > 1) {
      return { kind: 'needsMigration', eligible: false, reason: 'multiClass' }
    }
  }

  // Handle courses in migration process (v2 saved, v1 published)
  if (course.version === 2 && course.publishedVersion === 1) {
    return { kind: 'migrating' }
  }

  // Fully migrated (v2/v2) or other states - don't show badge
  return { kind: 'none' }
}

/**
 * Returns display properties for a given migration status
 * @param status - The migration status
 * @returns Display properties including icon type, label, color, and tooltip text
 */
export let getMigrationStatusDisplay = (
  status: MigrationStatus,
): MigrationStatusDisplay => {
  switch (status.kind) {
    case 'needsMigration':
      if (status.eligible) {
        return {
          iconType: 'warning',
          iconColor: 'warning.main',
          label: 'Needs Migration',
          color: 'warning',
          tooltipText:
            'This V1 course can be migrated to V2. Migration will preserve all content while enabling new features.',
        }
      }
      return {
        iconType: 'info',
        iconColor: 'error.main',
        label: 'Cannot Migrate (Multi-class)',
        color: 'error',
        tooltipText:
          'This V1 course contains multiple classes and cannot be automatically migrated to V2. Multi-class courses must remain on V1.',
      }

    case 'migrating':
      return {
        iconType: 'sync',
        iconColor: 'info.main',
        label: 'Migrating to V2',
        color: 'info',
        tooltipText:
          'This course is currently being migrated to V2. The published version remains on V1 until migration is complete.',
      }

    case 'none':
    default:
      // This case shouldn't be called, but return safe defaults
      return {
        iconType: 'info',
        iconColor: 'text.secondary',
        label: '',
        color: 'default',
        tooltipText: '',
      }
  }
}
